import {
  DestroyRef,
  Injectable,
  NgZone,
  effect,
  inject,
  isDevMode,
  signal,
  untracked,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ConfigStateService } from '@abp/ng.core';
import { resolveCurrentUserKey } from 'src/core/helpers/current-user-key.helper';
import { CallStatus } from '../models/call-status.enum';
import {
  CALL_TAB_CLEARED_REASON,
  CALL_TAB_SYNC_MESSAGE_TYPE,
  CallTabClearedReason,
  CallTabSyncMessage,
  CallTabSyncMessageType,
} from '../models/call-tab-sync-message.model';
import { CallFacadeService } from './call-facade.service';
import { TabNotificationService } from './tab-notification.service';

const CHANNEL_NAME = 'agenticcx-call-sync';

@Injectable({ providedIn: 'root' })
export class CallTabSyncService {
  private readonly facade = inject(CallFacadeService);
  private readonly configState = inject(ConfigStateService);
  private readonly tabNotification = inject(TabNotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  readonly tabId = createTabId();

  private channel: BroadcastChannel | null = null;
  private ownsActiveCall = false;
  private readonly documentHidden = signal(this.document.hidden);
  private readonly scheduledCallOfferAlerting = signal(false);

  constructor() {
    this.openChannel();

    const onVisibilityChange = (): void => this.documentHidden.set(this.document.hidden);
    this.document.addEventListener('visibilitychange', onVisibilityChange);

    effect(() => this.syncTabIndicators());

    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('visibilitychange', onVisibilityChange);
      this.closeChannel();
    });
  }

  /** Fed by ScheduledCallOfferAlertService; ranks below a ringing or live call (K-172). */
  setScheduledCallOfferAlert(alerting: boolean): void {
    this.scheduledCallOfferAlerting.set(alerting);
  }

  notifyAnswered(): void {
    this.post(CALL_TAB_SYNC_MESSAGE_TYPE.CallAnswered);
  }

  notifyRejected(): void {
    this.post(CALL_TAB_SYNC_MESSAGE_TYPE.CallCleared, CALL_TAB_CLEARED_REASON.Rejected);
  }

  private syncTabIndicators(): void {
    const status = this.facade.callStatus();

    const isInboundRinging = this.facade.isInboundRinging();
    const isOnCall = status === CallStatus.Active || status === CallStatus.Held;

    if (isInboundRinging) {
      this.tabNotification.startIncomingCall();
      return;
    }

    if (isOnCall) {
      if (!this.ownsActiveCall) {
        this.ownsActiveCall = true;
        untracked(() => this.post(CALL_TAB_SYNC_MESSAGE_TYPE.CallAnswered));
      }
      this.tabNotification.setActiveCall();
      return;
    }

    this.showRestingIndicator();

    if (this.ownsActiveCall) {
      this.ownsActiveCall = false;
      untracked(() => this.post(CALL_TAB_SYNC_MESSAGE_TYPE.CallEnded));
    }
  }

  private openChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      this.logWarn('BroadcastChannel unsupported — cross-tab call sync disabled');
      return;
    }

    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.addEventListener('message', event => {
      this.zone.run(() => this.onMessage(event.data as CallTabSyncMessage | null));
    });
  }

  private closeChannel(): void {
    this.channel?.close();
    this.channel = null;
  }

  private post(type: CallTabSyncMessageType, reason?: CallTabClearedReason): void {
    const userKey = this.currentUserKey();
    const extension = this.facade.agentExtension();

    if (!this.channel || !userKey || !extension) {
      return;
    }

    const message: CallTabSyncMessage = {
      type,
      tabId: this.tabId,
      userKey,
      extension,
      sipCallId: this.facade.currentSession()?.sipCallId ?? null,
      sentAt: Date.now(),
      ...(reason ? { reason } : {}),
    };

    try {
      this.channel.postMessage(message);
    } catch (err) {
      this.logWarn(`postMessage failed: ${String(err)}`);
    }
  }

  private onMessage(message: CallTabSyncMessage | null): void {
    if (!this.isForThisTab(message)) {
      return;
    }

    switch (message.type) {
      case CALL_TAB_SYNC_MESSAGE_TYPE.CallAnswered:
        // Deliberately does NOT release the offer. The two tabs are two registered contacts of one
        // agent, so a queue call forks to both; when one answers, FreeSWITCH cancels the other branch
        // by itself. Declining here only races that cancel — and on a still-ringing invitation the
        // only way to "release" is a SIP reject, which the backend records as the agent having
        // declined a call they in fact answered. Letting the cancel arrive keeps one attempt on
        // record instead of an answer plus a phantom rejection (TFS #153391 follow-up).
        break;
      case CALL_TAB_SYNC_MESSAGE_TYPE.CallCleared:
        // A genuine decline in the sibling tab: the person said no, so every endpoint of theirs must
        // stop ringing. Here the reject is correct — and required, because a decline on one branch
        // does not cancel the others, and the queue only moves on once the agent has declined.
        this.releaseLocalOffer();
        break;
      case CALL_TAB_SYNC_MESSAGE_TYPE.CallEnded:
        break;
    }

    if (!this.ownsActiveCall) {
      untracked(() => this.showRestingIndicator());
    }
  }

  /** Incoming > Active > (offer while hidden) > Default: a call reset never wipes a pending offer mark. */
  private showRestingIndicator(): void {
    if (this.scheduledCallOfferAlerting() && this.documentHidden()) {
      this.tabNotification.showScheduledCallOffer();
      return;
    }

    this.tabNotification.reset();
  }

  private isForThisTab(
    message: CallTabSyncMessage | null
  ): message is CallTabSyncMessage {
    if (!message || typeof message !== 'object' || !message.type) {
      return false;
    }

    if (message.tabId === this.tabId) {
      return false;
    }

    const userKey = this.currentUserKey();
    if (!userKey || message.userKey !== userKey) {
      return false;
    }

    const extension = this.facade.agentExtension();
    return !!extension && message.extension === extension;
  }

  private releaseLocalOffer(): void {
    if (!this.facade.isInboundRinging()) {
      return;
    }

    // Declined, not merely released. This branch only runs for a genuine decline in a sibling tab (see
    // the CallCleared case above), and each tab is a separate registered contact the call forked to, so
    // every one of them owes FreeSWITCH the same verdict - SIP 603. Releasing with a bare 480 here
    // would have the queue read the other branches as unanswered.
    void this.facade.declineIncoming();
  }

  private currentUserKey(): string | null {
    return resolveCurrentUserKey(this.configState.getDeep('currentUser'));
  }

  private logWarn(msg: string): void {
    if (isDevMode()) {
      console.warn(`[CallTabSyncService] ${msg}`);
    }
  }
}

function createTabId(): string {
  const cryptoRef = globalThis.crypto;
  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }

  return `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}
